import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useTablePrefs } from './useTablePrefs';
import { Modal, Button, FormField } from './ui';
import PivotView from './PivotView';
import { fieldLabel } from '../i18n/fieldMap';

// 1000+ satırda window-bazlı virtualization devreye girer.
// Bu eşiğin altında klasik render — küçük listelerde overhead yaratmaz.
const VIRTUALIZE_THRESHOLD = 1000;
// Ortalama satır yüksekliği (px) — measureElement ile dinamik düzeltilir.
const ROW_HEIGHT_ESTIMATE = 42;
// Görünür alanın üstüne+altına önden render edilecek satır sayısı (smooth scroll).
const OVERSCAN = 12;

/**
 * Paylasilan tablo bileseni: tiklama ile siralama, surukle-birak ile kolon sirasi,
 * "ayarlar" popover'i ile kolon goster/gizle. Tercihler localStorage'da pageKey
 * altinda saklanir.
 *
 * columns: [
 *   { id, header, accessor?, cell?(row,i), sortFn?(a,b), sortable?, hideable?, draggable?, width?, headerStyle?, cellStyle?, className?, headerClassName? }
 * ]
 */
export default function DataTable({
  pageKey,
  columns,
  data,
  loading,
  emptyText = 'Kayit bulunamadi',
  onRowClick,
  rowKey = 'id',
  rowClassName,
  className = '',
  tableClassName = '',
  // Bulk actions: [{ key, label, onClick(selectedIds, clearSelection), variant?, icon?, confirm? }]
  // variant: 'primary' | 'danger' | 'ghost'
  // confirm: string — onaylama mesajı (verilirse window.confirm sorulur)
  bulkActions = null,
  enablePivot = true, // PivotView görünür mü? (default açık — eski sayfalar etkilenmez)
  pivotConfig = null, // { defaultGroupBy, defaultValueKey, defaultAgg, groupableKeys, valueKeys }
}) {
  const { i18n } = useTranslation();
  // String kolon başlıklarını TR→EN map'ten otomatik çevir (fieldMap reuse)
  const colHeader = (col) => {
    const h = col.header;
    return typeof h === 'string' ? fieldLabel(h, i18n.language) : h;
  };
  const { prefs, setColumnOrder, setHiddenColumns, setSorting, setColumnWidth, resetColumnWidths, setFilter, clearFilters } = useTablePrefs(pageKey);
  const [showFilters, setShowFilters] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'pivot'
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPrompt, setBulkPrompt] = useState(null); // { action, value }
  const settingsRef = useRef(null);
  const dragColRef = useRef(null);
  const thRefs = useRef({});
  const hasBulk = Array.isArray(bulkActions) && bulkActions.length > 0;

  const getRowId = (row) => row && (row[rowKey] != null ? row[rowKey] : null);
  const clearSelection = () => setSelectedIds(new Set());
  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = (rows) => {
    setSelectedIds(prev => {
      const ids = rows.map(getRowId).filter(x => x != null);
      const allSelected = ids.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };
  const runBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    // Prompt'lu aksiyon: önce modal aç, kullanıcı değer girdikten sonra onayla
    if (action.prompt) {
      const p = action.prompt;
      let initial;
      if (Array.isArray(p.fields)) {
        // Multi-field: her field için defaultValue veya boş string
        initial = p.fields.reduce((acc, f) => ({ ...acc, [f.key]: f.defaultValue !== undefined ? f.defaultValue : '' }), {});
      } else {
        initial = p.allowEmpty && p.emptyValue !== undefined ? p.emptyValue : '';
      }
      setBulkPrompt({ action, value: initial });
      return;
    }
    if (action.confirm && !window.confirm(action.confirm)) return;
    const ids = Array.from(selectedIds);
    try {
      setBulkBusy(true);
      await action.onClick(ids, clearSelection);
    } finally {
      setBulkBusy(false);
    }
  };

  const submitPrompt = async () => {
    if (!bulkPrompt) return;
    const { action, value } = bulkPrompt;
    const p = action.prompt;

    // Validation
    if (Array.isArray(p.fields)) {
      for (const f of p.fields) {
        if (f.required && (value[f.key] == null || value[f.key] === '')) {
          alert(`"${f.label}" alanı zorunludur`);
          return;
        }
      }
    } else {
      if (!p.allowEmpty && (value == null || value === '')) {
        alert((p.label || 'Değer') + ' boş olamaz');
        return;
      }
    }

    if (action.confirm && !window.confirm(action.confirm)) return;
    const ids = Array.from(selectedIds);
    try {
      setBulkBusy(true);
      const finalValue = Array.isArray(p.fields) ? value : (value === '' ? null : value);
      await action.onClick(ids, clearSelection, finalValue);
      setBulkPrompt(null);
    } finally {
      setBulkBusy(false);
    }
  };

  // Kolon resize — header sağ kenarında handle ile drag
  const startResize = (e, colId) => {
    e.preventDefault();
    e.stopPropagation();
    const th = thRefs.current[colId];
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const onMove = (ev) => {
      const newW = startWidth + (ev.clientX - startX);
      setColumnWidth(colId, newW);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Settings popover dismiss-on-outside-click
  useEffect(() => {
    if (!showSettings) return;
    const onDoc = e => { if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettings(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showSettings]);

  // Etkin kolon sirasi: kullanicinin sirasi varsa onu kullan, eksikleri sona ekle
  const orderedColumns = useMemo(() => {
    const ids = columns.map(c => c.id);
    const userOrder = (prefs.columnOrder || []).filter(id => ids.includes(id));
    const remaining = ids.filter(id => !userOrder.includes(id));
    const finalOrder = [...userOrder, ...remaining];
    return finalOrder.map(id => columns.find(c => c.id === id)).filter(Boolean);
  }, [columns, prefs.columnOrder]);

  const visibleColumns = useMemo(() =>
    orderedColumns.filter(c => !(prefs.hiddenColumns || []).includes(c.id)),
    [orderedColumns, prefs.hiddenColumns]
  );

  // Filtreleme — kolon bazlı text filter (contains, case-insensitive)
  const filteredData = useMemo(() => {
    const filters = prefs.filters || {};
    const active = Object.entries(filters).filter(([, v]) => v != null && String(v).trim());
    if (!active.length || !data) return data || [];
    return data.filter(row => active.every(([colId, val]) => {
      const col = columns.find(c => c.id === colId);
      if (!col) return true;
      const getVal = typeof col.accessor === 'function' ? col.accessor : (r => r[col.accessor || col.id]);
      let cellVal = getVal(row);
      // cell render fonksiyonu varsa onun text'ini kullan (örn. badge'li hücreler için yine accessor değeri)
      if (cellVal == null) cellVal = '';
      const v = String(cellVal).toLocaleLowerCase('tr');
      return v.includes(String(val).toLocaleLowerCase('tr'));
    }));
  }, [data, prefs.filters, columns]);

  // Siralama
  const sortedData = useMemo(() => {
    if (!prefs.sorting || !filteredData) return filteredData || [];
    const { id, dir } = prefs.sorting;
    const col = columns.find(c => c.id === id);
    if (!col) return data;
    const getVal = col.sortFn
      ? null
      : (typeof col.accessor === 'function' ? col.accessor : (row => row[col.accessor || col.id]));
    const arr = [...filteredData];
    arr.sort((a, b) => {
      let cmp;
      if (col.sortFn) cmp = col.sortFn(a, b);
      else {
        const va = getVal(a), vb = getVal(b);
        if (va == null && vb == null) cmp = 0;
        else if (va == null) cmp = -1;
        else if (vb == null) cmp = 1;
        else if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
        else cmp = String(va).localeCompare(String(vb), 'tr');
      }
      return dir === 'desc' ? -cmp : cmp;
    });
    return arr;
  }, [filteredData, prefs.sorting, columns]);

  const onHeaderClick = col => {
    if (!col.sortable) return;
    const cur = prefs.sorting;
    if (!cur || cur.id !== col.id) setSorting({ id: col.id, dir: 'asc' });
    else if (cur.dir === 'asc') setSorting({ id: col.id, dir: 'desc' });
    else setSorting(null);
  };

  const onDragStart = (e, colId) => {
    dragColRef.current = colId;
    try { e.dataTransfer.setData('text/plain', colId); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = dragColRef.current;
    dragColRef.current = null;
    if (!sourceId || sourceId === targetId) return;
    const ids = orderedColumns.map(c => c.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, sourceId);
    setColumnOrder(next);
  };

  const toggleHidden = colId => {
    const cur = prefs.hiddenColumns || [];
    setHiddenColumns(cur.includes(colId) ? cur.filter(x => x !== colId) : [...cur, colId]);
  };

  const sortIndicator = col => {
    if (!col.sortable) return null;
    const s = prefs.sorting;
    const active = s && s.id === col.id;
    const arrow = active ? (s.dir === 'asc' ? '▲' : '▼') : '↕';
    return <span style={{ marginLeft: 4, fontSize: '0.7em', opacity: active ? 0.9 : 0.35 }}>{arrow}</span>;
  };

  // ─── Sanallaştırma — sadece 1000+ satırda devreye girer ──────────
  const tbodyRef = useRef(null);
  const shouldVirtualize = (sortedData?.length || 0) >= VIRTUALIZE_THRESHOLD && !loading;
  const [scrollMargin, setScrollMargin] = useState(0);
  useEffect(() => {
    if (!shouldVirtualize || !tbodyRef.current) return;
    const update = () => {
      const rect = tbodyRef.current?.getBoundingClientRect();
      if (rect) setScrollMargin(rect.top + window.scrollY);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [shouldVirtualize]);
  const virtualizer = useWindowVirtualizer({
    count: shouldVirtualize ? sortedData.length : 0,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: OVERSCAN,
    scrollMargin,
    measureElement: (el) => el?.getBoundingClientRect().height ?? ROW_HEIGHT_ESTIMATE,
  });
  const virtualItems = shouldVirtualize ? virtualizer.getVirtualItems() : [];
  const totalSize = shouldVirtualize ? virtualizer.getTotalSize() : 0;
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start - scrollMargin : 0;
  const paddingBottom = virtualItems.length > 0
    ? totalSize - virtualItems[virtualItems.length - 1].end
    : 0;

  return (
    <div className={'data-table-wrap ' + className}>
      {hasBulk && selectedIds.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)',
          borderRadius: 8, padding: '8px 14px', marginBottom: 8, flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--accent-primary, #3b82f6)' }}>
            ✓ {selectedIds.size} kayıt seçildi
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {bulkActions.map(action => {
              const variant = action.variant || 'primary';
              const styles = variant === 'danger'
                ? { bg: 'rgba(220,38,38,0.12)', border: 'rgba(220,38,38,0.45)', color: 'var(--danger)' }
                : variant === 'ghost'
                ? { bg: 'transparent', border: 'var(--border-secondary)', color: 'var(--text-secondary)' }
                : { bg: 'var(--accent-primary, #3b82f6)', border: 'var(--accent-primary, #3b82f6)', color: '#fff' };
              return (
                <button
                  key={action.key}
                  type="button"
                  disabled={bulkBusy || action.disabled}
                  onClick={() => runBulkAction(action)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: styles.bg, border: `1px solid ${styles.border}`, color: styles.color,
                    borderRadius: 6, padding: '5px 12px', fontSize: '0.82rem', fontWeight: 600,
                    cursor: bulkBusy || action.disabled ? 'not-allowed' : 'pointer',
                    opacity: bulkBusy || action.disabled ? 0.6 : 1,
                  }}
                >
                  {action.icon && <span>{action.icon}</span>}
                  {action.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={clearSelection}
              disabled={bulkBusy}
              style={{ background: 'transparent', border: '1px solid var(--border-secondary)', borderRadius: 6, padding: '5px 10px', fontSize: '0.82rem', color: 'var(--text-tertiary)', cursor: 'pointer' }}
            >Seçimi Temizle</button>
          </div>
        </div>
      )}
      {(() => {
        const activeFilterCount = Object.values(prefs.filters || {}).filter(v => v != null && String(v).trim()).length;
        const hasActive = activeFilterCount > 0;
        const expanded = showFilters || hasActive;
        return (
      <div className="data-table-toolbar" ref={settingsRef} style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        {hasActive && (
          <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginRight: 4 }}>
            {filteredData.length}/{(data || []).length} kayit
          </span>
        )}
        {enablePivot && (
          <div style={{ display: 'inline-flex', border: '1px solid var(--border-secondary)', borderRadius: 6, overflow: 'hidden', marginRight: 4 }}>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              title="Tablo görünümü"
              style={{
                padding: '3px 9px', fontSize: '0.74rem', border: 'none', cursor: 'pointer',
                background: viewMode === 'table' ? 'var(--accent-glow)' : 'transparent',
                color: viewMode === 'table' ? 'var(--accent-primary)' : 'var(--text-tertiary)',
                fontWeight: viewMode === 'table' ? 700 : 500,
              }}
            >☰ Tablo</button>
            <button
              type="button"
              onClick={() => setViewMode('pivot')}
              title="Pivot — gruplara ayır ve toplamla"
              style={{
                padding: '3px 9px', fontSize: '0.74rem', border: 'none', cursor: 'pointer', borderLeft: '1px solid var(--border-secondary)',
                background: viewMode === 'pivot' ? 'var(--accent-glow)' : 'transparent',
                color: viewMode === 'pivot' ? 'var(--accent-primary)' : 'var(--text-tertiary)',
                fontWeight: viewMode === 'pivot' ? 700 : 500,
              }}
            >📊 Pivot</button>
          </div>
        )}
        <button
          type="button"
          onClick={() => setShowFilters(s => !s)}
          title={expanded ? 'Filtreleri gizle' : 'Filtreleri göster'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: expanded ? 'rgba(59,130,246,0.12)' : 'transparent',
            border: '1px solid ' + (expanded ? 'rgba(59,130,246,0.4)' : 'var(--border-secondary)'),
            borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: '0.78rem',
            color: expanded ? 'var(--accent-primary, #3b82f6)' : 'var(--text-tertiary)',
          }}
        >
          🔍 Filtre
          {activeFilterCount > 0 && (
            <span style={{
              background: 'var(--accent-primary, #3b82f6)', color: '#fff',
              borderRadius: 99, padding: '0 6px', fontSize: '0.66rem', fontWeight: 700,
              minWidth: 16, textAlign: 'center', lineHeight: '14px',
            }}>{activeFilterCount}</span>
          )}
        </button>
        {hasActive && (
          <button
            type="button"
            onClick={() => clearFilters()}
            title="Tüm filtreleri temizle"
            style={{ background: 'transparent', border: '1px solid var(--border-secondary)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}
          >✕ Temizle</button>
        )}
        <button
          type="button"
          onClick={() => setShowSettings(s => !s)}
          title="Kolon ayarları"
          style={{ background: 'transparent', border: '1px solid var(--border-secondary)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}
        >⚙</button>
        {showSettings && (
          <div style={{ position: 'absolute', top: 28, right: 0, zIndex: 10, background: 'var(--bg-primary)', border: '1px solid var(--border-secondary)', borderRadius: 8, padding: 10, minWidth: 200, boxShadow: '0 4px 18px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: 6, fontWeight: 600 }}>GORUNUR KOLONLAR</div>
            {columns.filter(c => c.hideable !== false).map(c => {
              const hidden = (prefs.hiddenColumns || []).includes(c.id);
              return (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!hidden} onChange={() => toggleHidden(c.id)} />
                  {colHeader(c) || c.id}
                </label>
              );
            })}
            <div style={{ borderTop: '1px solid var(--border-secondary)', marginTop: 8, paddingTop: 8, fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
              Sürükle-bırak: kolon başlığını sürükleyerek yer değiştirin.
              Kolon kenarındaki çizgiyi sürükleyerek genişliği değiştirin.
            </div>
            <button
              type="button"
              onClick={() => resetColumnWidths()}
              style={{ marginTop: 6, width: '100%', background: 'transparent', border: '1px solid var(--border-secondary)', borderRadius: 6, padding: '4px 8px', fontSize: '0.74rem', cursor: 'pointer', color: 'var(--text-secondary)' }}
            >Kolon Genişliklerini Sıfırla</button>
            <button
              type="button"
              onClick={() => clearFilters()}
              style={{ marginTop: 4, width: '100%', background: 'transparent', border: '1px solid var(--border-secondary)', borderRadius: 6, padding: '4px 8px', fontSize: '0.74rem', cursor: 'pointer', color: 'var(--text-secondary)' }}
            >Filtreleri Temizle</button>
          </div>
        )}
      </div>
        );
      })()}

      {viewMode === 'pivot' ? (
        <PivotView
          data={filteredData}
          columns={visibleColumns}
          defaultGroupBy={pivotConfig?.defaultGroupBy}
          defaultValueKey={pivotConfig?.defaultValueKey}
          defaultAgg={pivotConfig?.defaultAgg}
          groupableKeys={pivotConfig?.groupableKeys}
          valueKeys={pivotConfig?.valueKeys}
          onRowClick={onRowClick}
        />
      ) : (
      <div
        className="data-table-scroll"
        tabIndex={0}
        onKeyDown={(e) => {
          // Klavyeyle yatay/dikey kaydırma — ok tuşları + Page/Home/End
          const el = e.currentTarget;
          const step = 80;
          let handled = true;
          switch (e.key) {
            case 'ArrowLeft':  el.scrollLeft -= step; break;
            case 'ArrowRight': el.scrollLeft += step; break;
            case 'ArrowUp':    el.scrollTop  -= step; break;
            case 'ArrowDown':  el.scrollTop  += step; break;
            case 'PageUp':     el.scrollTop  -= el.clientHeight; break;
            case 'PageDown':   el.scrollTop  += el.clientHeight; break;
            case 'Home':       if (e.ctrlKey) el.scrollTop = 0; else el.scrollLeft = 0; break;
            case 'End':        if (e.ctrlKey) el.scrollTop = el.scrollHeight; else el.scrollLeft = el.scrollWidth; break;
            default: handled = false;
          }
          if (handled) e.preventDefault();
        }}
        style={{ outline: 'none' }}
      >
      <table className={tableClassName} style={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          {hasBulk && <col style={{ width: 36 }} />}
          {visibleColumns.map(col => {
            const userW = prefs.columnWidths?.[col.id];
            const w = userW || col.width;
            return <col key={col.id} style={w ? { width: typeof w === 'number' ? w + 'px' : w } : undefined} />;
          })}
        </colgroup>
        <thead>
          <tr>
            {hasBulk && (() => {
              const visibleIds = sortedData.map(getRowId).filter(x => x != null);
              const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
              const someSelected = visibleIds.some(id => selectedIds.has(id));
              return (
                <th style={{ width: 36, padding: '4px 6px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                    onChange={() => toggleAllVisible(sortedData)}
                    title={allSelected ? 'Tüm seçimi kaldır' : 'Görünür kayıtların tümünü seç'}
                  />
                </th>
              );
            })()}
            {visibleColumns.map(col => (
              <th
                key={col.id}
                ref={(el) => { thRefs.current[col.id] = el; }}
                draggable={col.draggable !== false}
                onDragStart={col.draggable !== false ? e => onDragStart(e, col.id) : undefined}
                onDragOver={onDragOver}
                onDrop={e => onDrop(e, col.id)}
                onClick={() => onHeaderClick(col)}
                className={col.headerClassName || ''}
                style={{
                  cursor: col.sortable ? 'pointer' : (col.draggable !== false ? 'grab' : 'default'),
                  userSelect: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  ...(col.headerStyle || {}),
                }}
                title={col.draggable !== false ? 'Sürükle-bırak: yerleştir | Kenar: genişlik' : undefined}
              >
                {colHeader(col)}{sortIndicator(col)}
                {(prefs.filters && prefs.filters[col.id]) && (
                  <span title="Filtre aktif" style={{ marginLeft: 4, fontSize: '0.7em', color: '#3b82f6' }}>●</span>
                )}
                {col.resizable !== false && (
                  <span
                    onMouseDown={(e) => startResize(e, col.id)}
                    onClick={(e) => e.stopPropagation()}
                    onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    style={{
                      position: 'absolute', top: 0, right: 0, bottom: 0,
                      width: 6, cursor: 'col-resize',
                      userSelect: 'none',
                      zIndex: 2,
                    }}
                    title="Genişliği ayarlamak için sürükle"
                  >
                    <span style={{
                      position: 'absolute', top: '15%', bottom: '15%', right: 2,
                      width: 1, background: 'var(--border-secondary)',
                    }}></span>
                  </span>
                )}
              </th>
            ))}
          </tr>
          {showFilters && (
            <tr>
              {hasBulk && <th style={{ padding: '4px 4px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-secondary)' }} />}
              {visibleColumns.map(col => (
                <th key={col.id + '-f'} style={{ padding: '4px 4px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-secondary)' }}>
                  {col.filterable !== false ? (
                    <input
                      type="text"
                      value={(prefs.filters && prefs.filters[col.id]) || ''}
                      onChange={(e) => setFilter(col.id, e.target.value)}
                      placeholder="ara..."
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%', fontSize: '0.74rem', padding: '3px 6px',
                        border: '1px solid var(--border-secondary)', borderRadius: 4,
                        background: 'var(--bg-primary)', color: 'var(--text-primary)',
                        boxSizing: 'border-box',
                      }}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody ref={tbodyRef}>
          {loading ? (
            <tr><td colSpan={visibleColumns.length + (hasBulk ? 1 : 0)} style={{ textAlign: 'center', padding: '24px 12px', color: 'var(--text-tertiary)' }}>Yukleniyor...</td></tr>
          ) : (sortedData.length === 0) ? (
            <tr><td colSpan={visibleColumns.length + (hasBulk ? 1 : 0)} style={{ textAlign: 'center', padding: '24px 12px', color: 'var(--text-tertiary)' }}>{emptyText}</td></tr>
          ) : shouldVirtualize ? (
            <>
              {paddingTop > 0 && (
                <tr aria-hidden="true" style={{ height: paddingTop }}>
                  <td colSpan={visibleColumns.length + (hasBulk ? 1 : 0)} style={{ padding: 0, border: 0 }} />
                </tr>
              )}
              {virtualItems.map(vItem => {
                const row = sortedData[vItem.index];
                if (!row) return null;
                const rid = getRowId(row);
                const checked = rid != null && selectedIds.has(rid);
                return (
                  <tr
                    key={rid != null ? rid : vItem.key}
                    data-index={vItem.index}
                    ref={virtualizer.measureElement}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={typeof rowClassName === 'function' ? rowClassName(row) : rowClassName}
                    style={onRowClick ? { cursor: 'pointer', background: checked ? 'rgba(59,130,246,0.05)' : undefined } : (checked ? { background: 'rgba(59,130,246,0.05)' } : undefined)}
                  >
                    {hasBulk && (
                      <td style={{ width: 36, padding: '4px 6px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={checked} onChange={() => rid != null && toggleRow(rid)} />
                      </td>
                    )}
                    {visibleColumns.map(col => (
                      <td key={col.id} className={col.className || ''} style={col.cellStyle || undefined}>
                        {col.cell ? col.cell(row, vItem.index) : (typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor || col.id])}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {paddingBottom > 0 && (
                <tr aria-hidden="true" style={{ height: paddingBottom }}>
                  <td colSpan={visibleColumns.length + (hasBulk ? 1 : 0)} style={{ padding: 0, border: 0 }} />
                </tr>
              )}
            </>
          ) : sortedData.map((row, i) => {
            const rid = getRowId(row);
            const checked = rid != null && selectedIds.has(rid);
            return (
            <tr
              key={rid != null ? rid : i}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={typeof rowClassName === 'function' ? rowClassName(row) : rowClassName}
              style={onRowClick ? { cursor: 'pointer', background: checked ? 'rgba(59,130,246,0.05)' : undefined } : (checked ? { background: 'rgba(59,130,246,0.05)' } : undefined)}
            >
              {hasBulk && (
                <td style={{ width: 36, padding: '4px 6px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => rid != null && toggleRow(rid)}
                  />
                </td>
              )}
              {visibleColumns.map(col => (
                <td
                  key={col.id}
                  className={col.className || ''}
                  style={col.cellStyle || undefined}
                >
                  {col.cell ? col.cell(row, i) : (typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor || col.id])}
                </td>
              ))}
            </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      )}

      {bulkPrompt && (() => {
        const { action, value } = bulkPrompt;
        const p = action.prompt || {};
        const setValue = (v) => setBulkPrompt(prev => prev ? { ...prev, value: v } : prev);
        const setFieldValue = (key, v) => setBulkPrompt(prev => prev ? { ...prev, value: { ...prev.value, [key]: v } } : prev);

        // Tek bir alan rendere yardımcısı (hem multi-field hem single için)
        const renderInput = (f, val, onChange) => {
          const visibleIf = f.visibleIf;
          if (typeof visibleIf === 'function' && !visibleIf(value)) return null;
          return (
            <FormField key={f.key || 'value'} label={f.label || 'Değer'} hint={f.hint}>
              {f.type === 'select' ? (
                <select value={val ?? ''} onChange={e => onChange(e.target.value)} autoFocus={f.autoFocus}>
                  {f.allowEmpty && (
                    <option value="">{f.emptyLabel || '— boş —'}</option>
                  )}
                  {(f.options || []).map(o => (
                    <option key={o.value ?? '__'} value={o.value ?? ''}>{o.label}</option>
                  ))}
                </select>
              ) : f.type === 'number' ? (
                <input type="number" step={f.step || 'any'} min={f.min} max={f.max}
                  value={val ?? ''} onChange={e => onChange(e.target.value)}
                  placeholder={f.placeholder || ''} autoFocus={f.autoFocus} />
              ) : f.type === 'date' ? (
                <input type="date" value={val ?? ''} onChange={e => onChange(e.target.value)} autoFocus={f.autoFocus} />
              ) : f.type === 'checkbox' ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!val} onChange={e => onChange(e.target.checked)} />
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{f.checkboxLabel || f.label}</span>
                </label>
              ) : (
                <input type="text" value={val ?? ''} onChange={e => onChange(e.target.value)}
                  placeholder={f.placeholder || ''} autoFocus={f.autoFocus} />
              )}
            </FormField>
          );
        };

        const isMulti = Array.isArray(p.fields);

        return (
          <Modal
            title={p.title || action.label}
            subtitle={`${selectedIds.size} kayıt seçili`}
            size={p.size || 'sm'}
            onClose={() => !bulkBusy && setBulkPrompt(null)}
            closeOnOverlay={false}
            primaryAction={{ label: p.submitLabel || 'Uygula', onClick: submitPrompt, loading: bulkBusy }}
            cancelAction={{ label: 'Vazgeç', onClick: () => setBulkPrompt(null), disabled: bulkBusy }}
          >
            <div style={{ display: 'grid', gap: 10 }}>
              {p.description && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{p.description}</div>
              )}
              {isMulti
                ? p.fields.map((f, idx) =>
                    renderInput({ ...f, autoFocus: idx === 0 }, value[f.key], (v) => setFieldValue(f.key, v))
                  )
                : renderInput({ ...p, autoFocus: true }, value, setValue)}
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}
